/**
 * AdvertPreviewDialog.js — Shows how a scheduled/queued advert will appear.
 * Renders title, caption, media, target platforms, and scheduled times per platform.
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Facebook, Instagram, Music2, Youtube, Eye, Image as ImageIcon } from 'lucide-react';

const PLATFORM_META = {
  facebook: { icon: Facebook, name: 'Facebook', color: 'text-blue-600' },
  instagram: { icon: Instagram, name: 'Instagram', color: 'text-pink-600' },
  tiktok: { icon: Music2, name: 'TikTok', color: 'text-gray-900' },
  youtube: { icon: Youtube, name: 'YouTube', color: 'text-red-600' },
};

const STATE_LABEL = {
  queued: { text: 'In Wagry', cls: 'bg-amber-100 text-amber-800' },
  sent: { text: 'In Buffer', cls: 'bg-purple-100 text-purple-800' },
  published: { text: 'Gepubliseer', cls: 'bg-green-100 text-green-800' },
  failed: { text: 'Misluk', cls: 'bg-red-100 text-red-800' },
  cancelled: { text: 'Gekanselleer', cls: 'bg-gray-200 text-gray-700' },
};

const formatDateAf = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('af-ZA', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Johannesburg',
  });
};

export default function AdvertPreviewDialog({ advert, open, onClose }) {
  if (!advert) return null;
  const mediaType = advert.media_type;
  const mediaUrl = advert.media_url;
  const queuePlan = advert.queue_plan || {};

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="advert-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-700">
            <Eye className="h-5 w-5" /> Voorskou: {advert.title}
          </DialogTitle>
          <DialogDescription>
            Hierdie is hoe die advertensie sal lyk wanneer dit op sosiale media verskyn.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Media */}
          <div className="border rounded-lg overflow-hidden bg-gray-50">
            {!mediaUrl ? (
              <div className="p-6 text-center text-gray-400 flex flex-col items-center gap-2">
                <ImageIcon className="h-8 w-8" />
                <p className="text-sm">Geen media gekoppel nie.</p>
              </div>
            ) : mediaType === 'image' ? (
              <img src={mediaUrl} alt="advert preview" className="w-full max-h-96 object-contain bg-black" data-testid="preview-media-img" />
            ) : mediaType === 'video' ? (
              <video src={mediaUrl} controls className="w-full max-h-96" data-testid="preview-media-video" />
            ) : mediaType === 'audio' ? (
              <audio src={mediaUrl} controls className="w-full p-3" data-testid="preview-media-audio" />
            ) : (
              <p className="p-4 text-sm text-gray-500">Onbekende media-tipe: {mediaType}</p>
            )}
          </div>

          {/* Caption */}
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1">BYSKRIF</p>
            <div className="bg-white border rounded p-3 text-sm whitespace-pre-wrap" data-testid="preview-caption">
              {advert.caption || <em className="text-gray-400">geen byskrif nie</em>}
            </div>
            <p className="text-xs text-gray-400 mt-1">{(advert.caption || '').length} karakters</p>
          </div>

          {/* Per-platform schedule */}
          <div>
            <p className="text-xs font-semibold text-gray-500 mb-1">PLATFORMS &amp; STAAT</p>
            <div className="space-y-2">
              {(advert.platforms || []).map(p => {
                const meta = PLATFORM_META[p];
                const Icon = meta?.icon;
                const info = queuePlan[p];
                const state = info?.state;
                const stateMeta = state && STATE_LABEL[state];
                const targetDate = info?.target_date || advert.scheduled_for;
                return (
                  <div key={p} className="flex items-center justify-between gap-2 border rounded p-2 bg-white" data-testid={`preview-platform-${p}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      {Icon && <Icon className={`h-4 w-4 ${meta.color} flex-shrink-0`} />}
                      <span className="font-medium">{meta?.name || p}</span>
                      {stateMeta && (
                        <Badge className={`${stateMeta.cls} text-[10px]`}>{stateMeta.text}</Badge>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 text-right">
                      {targetDate ? formatDateAf(targetDate) : 'Geen datum'}
                    </div>
                  </div>
                );
              })}
              {(advert.platforms || []).length === 0 && (
                <p className="text-sm text-gray-400">Geen platforms gekies nie.</p>
              )}
            </div>
          </div>

          {/* Errors per platform */}
          {Object.entries(queuePlan).filter(([, info]) => info?.error).length > 0 && (
            <div className="border border-red-200 bg-red-50 rounded p-3">
              <p className="text-xs font-semibold text-red-700 mb-1">FOUTE</p>
              {Object.entries(queuePlan).filter(([, info]) => info?.error).map(([p, info]) => (
                <p key={p} className="text-xs text-red-700">
                  <strong>{PLATFORM_META[p]?.name || p}:</strong> {info.error}
                </p>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
